import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  applyLanguage,
  readStoredLanguagePreference,
  resolveAnonymousLanguage,
  saveProfileLanguage,
  syncLanguageForUser,
  writeStoredLanguagePreference,
} from '../lib/languagePreference'
import { isSupportedLanguage, type AppLanguage } from '../i18n/languages'
import { supabase } from '../lib/supabaseClient'

export function useUserLanguage(userId?: string | null) {
  const { i18n } = useTranslation()
  const [language, setLanguage] = useState<AppLanguage>(() => {
    const fromI18n = isSupportedLanguage(i18n.language) ? i18n.language : null
    return fromI18n ?? readStoredLanguagePreference() ?? resolveAnonymousLanguage()
  })
  const [loading, setLoading] = useState(Boolean(userId))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true

    const sync = async () => {
      if (!userId) {
        const anonymousLanguage = resolveAnonymousLanguage()
        await applyLanguage(anonymousLanguage)
        if (mounted) {
          setLanguage(anonymousLanguage)
          setLoading(false)
        }
        return
      }

      setLoading(true)
      const { data: userData } = await supabase.auth.getUser()
      const resolved = userData.user
        ? await syncLanguageForUser(userData.user)
        : resolveAnonymousLanguage()

      if (mounted) {
        setLanguage(resolved)
        setLoading(false)
      }
    }

    void sync()

    return () => {
      mounted = false
    }
  }, [userId])

  const changeLanguage = useCallback(async (nextLanguage: AppLanguage) => {
    setError(null)
    setLanguage(nextLanguage)
    writeStoredLanguagePreference(nextLanguage)
    await applyLanguage(nextLanguage)

    if (!userId) return null

    const saveError = await saveProfileLanguage(userId, nextLanguage)
    if (saveError) {
      setError(saveError)
      return saveError
    }

    return null
  }, [userId])

  return { language, loading, error, changeLanguage }
}
